import type { components } from "@roastery/api-client";
import { useQuery } from "@tanstack/react-query";
import { runtimeConfig } from "../runtime-config";
import { tenantAddress } from "../tenant-address";

export type TenantBranding = components["schemas"]["TenantBranding"];

/**
 * How the roastery named by the address looks (US-10.5), fetched **without a token**.
 *
 * <p>This is the only call the portal makes before signing in, and it is the reason
 * `/api/v1/public/**` exists: the screen on the way to Keycloak, and the one that says the access has
 * ended, both have to name a roastery no token is going to identify. A slug nobody trades under
 * answers 404 and the portal simply stays unbranded — an address typed wrong is not an error worth
 * a screen of its own.
 */
export function usePublicBranding() {
  const slug = tenantAddress().slug;
  return useQuery({
    queryKey: ["public-branding", slug],
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async (): Promise<TenantBranding | null> => {
      const response = await fetch(`${runtimeConfig.apiUrl}/api/v1/public/tenants/${slug}/branding`);
      return response.ok ? ((await response.json()) as TenantBranding) : null;
    },
  });
}

/** What the portal paints itself with, whichever of the two answers it has. */
export type RoasteryLook = {
  name?: string;
  logoUrl?: string | null;
  primaryColor?: string | null;
};

/**
 * Paints the page in the roastery's colour and puts its name in the browser tab.
 *
 * <p>Only the two tokens that carry a brand are touched — the primary colour and the text that sits
 * on it. Everything else stays the product's, because a roastery choosing one colour is not choosing
 * a whole design system, and a badly picked hue must not be able to make its own portal unreadable.
 * The text on the colour is decided by its luminance rather than left to the roastery: black on a
 * dark brown is not a choice anybody meant to make.
 */
export function applyBranding(look: RoasteryLook): void {
  const root = document.documentElement;
  const colour = look.primaryColor ?? "";
  if (/^#[0-9a-fA-F]{6}$/.test(colour)) {
    root.style.setProperty("--color-primary", colour);
    root.style.setProperty("--color-primary-foreground", readableOn(colour));
    root.style.setProperty("--color-ring", colour);
  } else {
    root.style.removeProperty("--color-primary");
    root.style.removeProperty("--color-primary-foreground");
    root.style.removeProperty("--color-ring");
  }
  if (look.name) {
    document.title = look.name;
  }
}

/**
 * Black or white, whichever a reader can actually see on this colour. WCAG relative luminance with
 * the sRGB transfer curve; the 0.45 cut is where white and black contrast about equally.
 */
function readableOn(hex: string): string {
  const channel = (offset: number) => {
    const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
  return luminance > 0.45 ? "#111111" : "#ffffff";
}
